import { DriverVisibility, type IdentificationMethod, type Sort, type TimestampInMillis, type UUID } from '@cobliteam/common-types';
import { Location } from '@cobliteam/common-types';
import useCameraTripImage from '@cobliteam/hooks/src/useCamera/useCameraTripImage';
import Icon from '@cobliteam/ui/icons';
import { subDays } from '@cobliteam/utils/date';
import { deviceStatusLabel } from '../../../helpers';
import { useSearch } from '../../../hooks/useSearch';
import { type DeviceWithCategory, type DriverWithCategory, EnumCategory, type VehicleWithCategory } from '../../../types';
import CardHeader from '../../Cards/CardHeader';
import CardImages, { type CardImagesProps } from '../../Cards/CardImage';
import CardItem, { type CardItemProps } from '../../Cards/CardItem';
import CardMap from '../../Cards/CardMap';

const ONE_WEEK_AGO = subDays(new Date(), 7).getTime();
const TODAY = new Date().getTime();

type TripImageFilters = {
  fromTimestamp?: TimestampInMillis;
  toTimestamp?: TimestampInMillis;
  identificationMethod?: IdentificationMethod[];
  driverVisibilities?: DriverVisibility[];
  vehicleIds?: UUID[];
  driverIds?: UUID[];
  isObstructed?: boolean | null;
  page?: number;
  size?: number;
  sort?: Sort;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const DriverDetails = () => {
  const { data, selectedItem } = useSearch();
  const item = selectedItem as DriverWithCategory;

  const tripImageFilters: TripImageFilters = {
    fromTimestamp: ONE_WEEK_AGO,
    toTimestamp: TODAY,
    identificationMethod: undefined,
    driverVisibilities: [DriverVisibility.VISIBLE],
    vehicleIds: undefined,
    driverIds: [item.id],
    isObstructed: false,
    page: 1,
    size: 3,
  };

  const { data: tripImage, isLoading: isLoadingTripImage } = useCameraTripImage().getTripImage(
    tripImageFilters.fromTimestamp,
    tripImageFilters.toTimestamp,
    tripImageFilters.identificationMethod,
    tripImageFilters.driverVisibilities,
    tripImageFilters.vehicleIds,
    tripImageFilters.driverIds,
    tripImageFilters.isObstructed,
    tripImageFilters.page,
    tripImageFilters.size
  );

  if (!item) return null;

  const licenseNumber = item?.driverLicense || '---';
  const licenseCategory = item?.licenseCategory || '---';
  const licenseExpirationDate = item?.licenseExpirationDateInMillis ? new Date(item.licenseExpirationDateInMillis).toLocaleDateString('pt-BR') : '---';
  const allVehicles = data?.filter((d) => d.category.id === EnumCategory.VEHICLES) as VehicleWithCategory[];
  const vehicle = allVehicles?.find((d) => d.licensePlate && d.licensePlate === item?.vehicle?.licensePlate) || null;
  const allDevices = data?.filter((d) => d.category.id === EnumCategory.DEVICES) as DeviceWithCategory[];
  const device = allDevices?.find((d) => d.vehicle && d.vehicle?.id === vehicle?.id) || null;

  const tokenAlias = item?.identificationToken ? item?.identificationToken.tokenAlias : '---';

  const cardVehicle: CardItemProps = {
    disabled: !vehicle,
    title: vehicle ? vehicle?.licensePlate || '---' : 'Sem veículo associado',
    subtitle: vehicle
      ? `${vehicle.model.brand || vehicle.model.name || vehicle.model.year ? '' : '---'} ${vehicle.model.brand ? `${vehicle.model.brand} - ` : ''} ${vehicle.model.name ? `${vehicle.model.name} - ` : ''} ${vehicle.model.year ? `(${vehicle.model.year})` : ''}`
      : '---',
    tooltip: vehicle ? 'Veículo associado' : 'Nenhum veículo associado',
    icon: 'truck',
    color: 'primary',
    itemLink: `${Location.VEHICLES}?search=${vehicle?.id}`,
    pageLink: `${Location.VEHICLES}`,
  };

  const cardDevice: CardItemProps = {
    disabled: !device?.id,
    title: device?.cobliId ? `${device?.cobliId} - ${device?.generalType || '---'}` : 'Sem dispositivo',
    tooltip: device?.id ? 'Dispositivo associado' : 'Dispositivo não associado',
    subtitle: device?.generalStatus ? `Status: ${deviceStatusLabel(device.generalStatus)}` : '---',
    icon: 'router',
    color: 'primary',
    itemLink: `${Location.DEVICES}?tab=${device?.id ? 'Associado' : 'Desassociado'}&search=${device?.id}`,
    pageLink: undefined,
  };

  const cardImages: CardImagesProps = {
    isLoading: isLoadingTripImage,
    notFoundMessage: 'Nenhuma imagens associadas ao motorista.',
    notFoundLinkLabel: 'Ir para identificação facial',
    notFoundLink: `${Location.CAMERA_TRIP_IMAGE_HISTORY_ALL.replace(':view', 'all')}?driverIds=${item.id}`,
    moreLink: `${Location.CAMERA_TRIP_IMAGE_HISTORY_ALL.replace(':view', 'all')}?driverIds=${item.id}`,
    moreLinkLabel: 'Ver mais',
    images:
      tripImage?.content?.map((content) => {
        return {
          url: content.tripImageSample?.imageUrl,
          alt: content.tripImageSample?.id,
        };
      }) || [],
  };

  const subtitle = (
    <>
      <Icon name='key-round' className='!size-3' /> Identificador: {tokenAlias}
    </>
  );

  return (
    <div className='relative flex h-[500px] w-full flex-col gap-4 overflow-hidden overflow-y-auto p-4'>
      {/*Header*/}
      <CardHeader
        icon={item.category.icon}
        color={item.category.color}
        title={item.name}
        subtitle={subtitle}
        link={`${Location.DRIVER_MANAGEMENT}?search=${item.id}`}
        linkTooltip='Ir para página de motoristas'
      />

      {/*Images*/}
      <div>
        <CardImages {...cardImages} />
      </div>

      {/*Details*/}
      <div>
        <div className='flex items-center gap-1 text-xs'>
          <Icon name='smartphone' className='!size-3' /> Código de acesso ao aplicativo:
          <span className='font-medium'>{item.driverCode || '---'}</span>
        </div>
      </div>
      <div className='grid grid-cols-12 gap-2'>
        <div className='col-span-6'>
          <div className='mb-1 flex items-center gap-2 font-medium text-xs'>
            <Icon name='contact-round' className='!size-3' />
            Habilitação
          </div>
          <div className='ml-5 w-full'>
            <div className='text-xs'>Numero: {licenseNumber}</div>
            <div className='text-xs'>Categoria: {licenseCategory}</div>
            <div className='text-xs'>Validade: {licenseExpirationDate}</div>
          </div>
        </div>
        <div className='col-span-6'>
          <div className='mb-1 flex flex-1 items-center gap-2 font-medium text-xs'>
            <Icon name='info' className='!size-3' />
            Informações
          </div>
          <div className='ml-5 w-full'>
            {item.phoneNumbers.map((phone, index) => (
              <a href={`tel:${phone}`} key={index + phone} className='font-normal text-xs'>
                Tel.: {phone ? phone : '---'}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/*Cards*/}
      <div className=' space-y-2'>
        <CardItem {...cardVehicle} />
        <CardItem {...cardDevice} />
      </div>

      {/*Map*/}
      <div>
        <CardMap
          title='Localização do dispositivo'
          latitude={device?.lastLocation?.latitude}
          longitude={device?.lastLocation?.longitude}
          updatedAt={device?.lastLocation?.cobliIngestedAt}
          linkTo={`/monitoring?search=${vehicle?.licensePlate}&focus=true`}
        />
      </div>
    </div>
  );
};

export default DriverDetails;
